import React, { useCallback, useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Box,
  Chip,
  Divider,
  List,
  Stack,
  Tooltip,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import api, { clearAuthAndRedirect } from '../../services/api';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { buildBackendUrl } from '../../config/env';
import { useTenant } from '../../tenancy/TenantProvider';
import {
  formatDateOnly,
  formatDateTime,
  getNotificationActionHint,
  getNotificationActionLabel,
  getNotificationCategory,
  getNotificationMessage,
  getNotificationTitle,
  getNotificationTone,
  isExternalNotificationAction,
  isAnnouncementActive,
  isHolidayUpcoming,
  isNavbarNotificationVisible,
  resolveNotificationAction,
} from '../../utils/notificationUtils';

// Per-category icon for the navbar notification dropdown (category comes from
// notificationUtils — kept in sync with the /notifications page).
const NAVBAR_NOTIFICATION_ICONS = {
  vacation: FlightTakeoffOutlinedIcon,
  request: DescriptionOutlinedIcon,
  exit_entry: MeetingRoomOutlinedIcon,
  appraisal: AssignmentTurnedInOutlinedIcon,
  announcement: CampaignIcon,
  holiday: EventIcon,
  asset: DevicesOtherOutlinedIcon,
  balance: AccountBalanceWalletOutlinedIcon,
  default: NotificationsIcon,
};

function Header({
  onMenuClick,
  isMobile,
  isExpanded,
  drawerWidthExpanded = 240,
  drawerWidthCollapsed = 72,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { resolvedMode, toggleMode } = useThemeMode();
  const { branding } = useTenant();
  const role = localStorage.getItem('role') || 'employee';

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const announcementsState = useSelector((state) => state.announcements.announcements);
  const holidaysState = useSelector((state) => state.announcements.holidays);
  const announcements = Array.isArray(announcementsState) ? announcementsState : [];
  const holidays = Array.isArray(holidaysState) ? holidaysState : [];

  const userName = localStorage.getItem('name') || 'User';
  const avatarUrl = buildBackendUrl(localStorage.getItem('avatar_url')) || '';
  const headerTitle = branding?.product_label || branding?.display_name || 'HR Workspace';
  const headerSubtitle = branding?.display_name
    ? `${branding.display_name} workforce operations and approvals dashboard`
    : 'Workforce operations and approvals dashboard';

  const now = new Date();
  const filteredAnnouncements = announcements.filter((ann) => isAnnouncementActive(ann, now));
  const filteredHolidays = holidays.filter((holiday) => isHolidayUpcoming(holiday, now));
  const navbarNotifications = notifications.filter((notification) =>
    isNavbarNotificationVisible(notification, {
      announcements,
      holidays,
      now,
    })
  );
  const visibleNavbarNotifications = navbarNotifications.slice(0, 5);
  const remainingNavbarNotifications = Math.max(navbarNotifications.length - visibleNavbarNotifications.length, 0);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const pollId = window.setInterval(fetchNotifications, 60000);

    return () => {
      window.clearInterval(pollId);
    };
  }, [fetchNotifications]);

  const handleOpenUserMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorEl(null);
  const handleOpenNotifMenu = (event) => setNotifAnchorEl(event.currentTarget);
  const handleCloseNotifMenu = () => setNotifAnchorEl(null);

  const openNotificationAction = (action) => {
    handleCloseNotifMenu();
    if (isExternalNotificationAction(action)) {
      window.open(action, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(action || '/notifications');
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await api.post(`/notifications/${notifId}/mark-as-read`);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notifId ? { ...notif, read: true, read_at: new Date().toISOString() } : notif
        )
      );
      window.refreshNotificationUnreadCount?.();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllVisibleAsRead = async () => {
    const ids = navbarNotifications.map((item) => item.id).filter(Boolean);
    if (!ids.length) return;

    try {
      await api.post('/notifications/bulk-read', { ids });
      const nowIso = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((item) => (ids.includes(item.id) ? { ...item, read: true, read_at: item.read_at || nowIso } : item))
      );
      window.refreshNotificationUnreadCount?.();
    } catch (err) {
      console.error('Error marking visible notifications as read:', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      await handleMarkAsRead(notif.id);
    }
    const action = resolveNotificationAction(notif, role);
    openNotificationAction(action);
  };

  const toneColor = (tone) => {
    switch (tone) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  // Type-specific icon + status-tinted avatar for one dropdown row.
  const renderNotificationAvatar = (notif) => {
    const IconComponent = NAVBAR_NOTIFICATION_ICONS[getNotificationCategory(notif)] || NotificationsIcon;
    const color = toneColor(getNotificationTone(notif));

    return (
      <Avatar
        sx={{
          width: 34,
          height: 34,
          bgcolor: notif.read ? alpha(theme.palette.text.primary, 0.08) : alpha(color, 0.16),
          color: notif.read ? theme.palette.text.secondary : color,
        }}
      >
        <IconComponent fontSize="small" />
      </Avatar>
    );
  };

  const unreadNotificationsCount = navbarNotifications.length;
  const unreadCount =
    unreadNotificationsCount + filteredAnnouncements.length + filteredHolidays.length;

  const handleLogout = () => {
    clearAuthAndRedirect();
  };

  const drawerSpace = isMobile ? 0 : isExpanded ? drawerWidthExpanded : drawerWidthCollapsed;
  const sidebarToggleLabel = isMobile ? 'Open menu' : isExpanded ? 'Collapse sidebar' : 'Expand sidebar';
  const darkMode = resolvedMode === 'dark';
  const navBrand = {
    navy: theme.palette.primary.dark,
    blue: theme.palette.primary.main,
    cyan: theme.palette.secondary.main,
    green: theme.palette.success.main,
  };
  const navIconButtonSx = {
    color: darkMode ? alpha('#ffffff', 0.82) : theme.palette.text.secondary,
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: 1.5,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor: darkMode ? alpha('#ffffff', 0.1) : alpha(theme.palette.text.primary, 0.08),
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
    },
    '&.Mui-focusVisible': {
      boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, darkMode ? 0.42 : 0.28)}`,
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        ml: { lg: `${drawerSpace}px` },
        width: { lg: `calc(100% - ${drawerSpace}px)` },
        overflow: 'hidden',
        backgroundImage: 'none',
        // Dark: the sidebar's exact navy plane (same #0d1626 base + soft blue
        // glow) so the top bar and the rail read as one continuous chrome across
        // the seam. Light: a flat near-white content surface, separated from the
        // page canvas by a single hairline — no rainbow bar, no cyan, no shadow.
        background: darkMode
          ? `radial-gradient(680px 420px at 28px -40px, ${alpha(navBrand.blue, 0.32)}, transparent 70%), #0d1626`
          : '#ffffff',
        backgroundColor: darkMode ? '#0d1626' : '#ffffff',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
        borderBottom: `1px solid ${darkMode ? alpha('#ffffff', 0.07) : theme.hr.line.soft}`,
        boxShadow: 'none',
        transition: theme.transitions.create(['margin-left', 'width'], {
          duration: theme.transitions.duration.short,
        }),
      }}
    >
      <Toolbar
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: { xs: 64, sm: 70 },
          gap: 1.1,
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Tooltip title={sidebarToggleLabel}>
          <IconButton
            onClick={onMenuClick}
            edge="start"
            sx={{ ...navIconButtonSx, mr: 0.5 }}
            aria-label={sidebarToggleLabel}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            noWrap
            sx={{
              lineHeight: 1.2,
              fontWeight: 700,
              color: darkMode ? '#ffffff' : theme.palette.text.primary,
            }}
          >
            {headerTitle}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
              lineHeight: 1.3,
              color: darkMode ? alpha('#ffffff', 0.7) : theme.palette.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {headerSubtitle}
          </Typography>
        </Box>

        <Tooltip title={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={toggleMode} sx={navIconButtonSx}>
            {resolvedMode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications">
          <IconButton onClick={handleOpenNotifMenu} sx={navIconButtonSx}>
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                  minWidth: 16,
                  height: 16,
                  fontSize: '0.66rem',
                  padding: '0 4px',
                  boxShadow: 'none',
                  border: 'none',
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Button
          onClick={handleOpenUserMenu}
          startIcon={
            <Avatar
              src={avatarUrl || undefined}
              alt={userName}
              sx={{
                width: 28,
                height: 28,
                bgcolor: navBrand.blue,
                border: `1px solid ${alpha('#ffffff', 0.22)}`,
              }}
            >
              {!avatarUrl ? userName.charAt(0) : null}
            </Avatar>
          }
          sx={{
            borderRadius: 999,
            px: 1.2,
            textTransform: 'none',
            color: darkMode ? alpha('#ffffff', 0.9) : theme.palette.text.primary,
            border: '1px solid',
            borderColor: darkMode ? alpha('#ffffff', 0.14) : theme.hr.line.soft,
            backgroundColor: 'transparent',
            display: { xs: 'none', sm: 'inline-flex' },
            transition: theme.transitions.create(['background-color', 'color', 'border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover': {
              backgroundColor: darkMode ? alpha('#ffffff', 0.1) : alpha(theme.palette.text.primary, 0.06),
              borderColor: darkMode ? alpha('#ffffff', 0.22) : alpha(theme.palette.text.primary, 0.16),
            },
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, darkMode ? 0.42 : 0.28)}`,
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <Typography variant="body2" sx={{ maxWidth: 120 }} noWrap>
            {userName}
          </Typography>
        </Button>

        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ ...navIconButtonSx, display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <Avatar
            src={avatarUrl || undefined}
            alt={userName}
            sx={{
              width: 30,
              height: 30,
              bgcolor: navBrand.blue,
              border: `1px solid ${alpha('#ffffff', 0.22)}`,
            }}
          >
            {!avatarUrl ? userName.charAt(0) : null}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={handleCloseNotifMenu}
          PaperProps={{
            sx: {
              width: 440,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 560,
              borderRadius: theme.hr.radius.md,
              border: `1px solid ${darkMode ? alpha('#ffffff', 0.08) : theme.hr.line.soft}`,
              boxShadow: theme.hr.shadow.soft,
              backgroundImage: 'none',
              mt: 1,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.35 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {unreadCount} active item{unreadCount === 1 ? '' : 's'}
                </Typography>
              </Box>
              {navbarNotifications.length > 0 ? (
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleMarkAllVisibleAsRead}
                  startIcon={<MarkEmailReadIcon fontSize="small" />}
                  sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
                >
                  Mark read
                </Button>
              ) : null}
            </Stack>
          </Box>
          <Divider />

          <List disablePadding sx={{ py: 0.75 }}>
            {visibleNavbarNotifications.length > 0 ? (
              visibleNavbarNotifications.map((notif) => {
                const actionLabel = getNotificationActionLabel(notif, role);
                const actionHint = getNotificationActionHint(notif, role);

                return (
                  <Box
                    component="li"
                    key={`notif-${notif.id}`}
                    onClick={() => handleNotificationClick(notif)}
                    sx={{
                      mx: 1,
                      mb: 0.75,
                      p: 1.25,
                      listStyle: 'none',
                      cursor: 'pointer',
                      border: `1px solid ${darkMode ? alpha('#ffffff', 0.1) : alpha(theme.palette.text.primary, 0.1)}`,
                      borderRadius: theme.hr.radius.sm,
                      bgcolor: notif.read
                        ? 'transparent'
                        : darkMode
                          ? alpha('#ffffff', 0.05)
                          : alpha(theme.palette.text.primary, 0.035),
                      transition: theme.transitions.create('background-color', {
                        duration: theme.transitions.duration.shortest,
                      }),
                      '&:hover': {
                        bgcolor: darkMode ? alpha('#ffffff', 0.08) : alpha(theme.palette.text.primary, 0.06),
                      },
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  >
                    <Stack direction="row" spacing={1.15} alignItems="flex-start">
                      {renderNotificationAvatar(notif)}

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                            {getNotificationTitle(notif)}
                          </Typography>
                          {!notif.read ? (
                            <Chip
                              size="small"
                              color="error"
                              label="New"
                              sx={{ height: 20, boxShadow: 'none', '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums' } }}
                            />
                          ) : null}
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                          {formatDateTime(notif.created_at) || 'No date'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.55,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {getNotificationMessage(notif)}
                        </Typography>

                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.9, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleNotificationClick(notif);
                            }}
                            startIcon={<OpenInNewIcon fontSize="small" />}
                          >
                            {actionLabel}
                          </Button>
                          {!notif.read ? (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMarkAsRead(notif.id);
                              }}
                            >
                              Dismiss
                            </Button>
                          ) : null}
                          {actionHint ? (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 170 }}>
                              {actionHint}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ px: 2, py: 2.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No unread notifications.
                </Typography>
              </Box>
            )}

            {remainingNavbarNotifications > 0 ? (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {remainingNavbarNotifications} more unread notification{remainingNavbarNotifications === 1 ? '' : 's'} in the full center.
                </Typography>
              </Box>
            ) : null}

            <Divider sx={{ my: 0.75 }} />

            {filteredAnnouncements.length > 0 ? (
              filteredAnnouncements.slice(0, 3).map((ann) => (
                <Box
                  component="li"
                  key={`ann-${ann.id}`}
                  onClick={() => openNotificationAction('/notifications?section=announcements')}
                  sx={{
                    mx: 1,
                    mb: 0.75,
                    p: 1.15,
                    listStyle: 'none',
                    cursor: 'pointer',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1.15} alignItems="flex-start">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'info.main' }}>
                      <CampaignIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                        {ann.title || 'Announcement'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {ann.message || 'No details available.'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ann.end_date ? `Until ${formatDateTime(ann.end_date)}` : 'Active announcement'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))
            ) : (
              <Box sx={{ px: 2, py: 1.2 }}>
                <Typography variant="body2" color="text.secondary">
                  No active announcements.
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 0.75 }} />

            {filteredHolidays.length > 0 ? (
              filteredHolidays.slice(0, 3).map((holiday) => (
                <Box
                  component="li"
                  key={`hol-${holiday.id}`}
                  onClick={() => openNotificationAction('/notifications?section=holidays')}
                  sx={{
                    mx: 1,
                    mb: 0.75,
                    p: 1.15,
                    listStyle: 'none',
                    cursor: 'pointer',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1.15} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'success.main' }}>
                      <EventIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                        {holiday.name || 'Holiday'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateOnly(holiday.holiday_date)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))
            ) : (
              <Box sx={{ px: 2, py: 1.2 }}>
                <Typography variant="body2" color="text.secondary">
                  No upcoming holidays.
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ px: 1, pb: 0.25 }}>
              <Button
                fullWidth
                variant="text"
                onClick={() => openNotificationAction('/notifications')}
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ justifyContent: 'space-between', fontWeight: 600 }}
              >
                View full notification center
              </Button>
            </Box>
          </List>
        </Menu>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseUserMenu}
          PaperProps={{
            sx: {
              minWidth: 180,
              borderRadius: theme.hr.radius.sm,
              border: `1px solid ${darkMode ? alpha('#ffffff', 0.08) : theme.hr.line.soft}`,
              boxShadow: theme.hr.shadow.soft,
              backgroundImage: 'none',
              mt: 1,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate('/profile');
            }}
          >
            <PersonOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
            Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              handleLogout();
            }}
            sx={{ '&:hover': { color: theme.palette.error.light, backgroundColor: alpha(theme.palette.error.main, 0.12) } }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Header;

